import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import {
  Box,
  Collapse,
  Drawer,
  Grid2,
  IconButton,
  Paper,
  Alert,
  Tooltip,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import dayjs, { Dayjs } from "dayjs";
import CustomTypography from "../../components/common/form/CustomTypography";
import CustomTextField from "../../components/common/textFields/TextField";
import { DateRangePickerNew } from "../../components/common/textFields/DatePickerRangeNew";
import AppSearchDropdownMulti from "../../components/common/filter/AppSearchDropdownMulti";
import { FilterValue } from "../../../models/Filters/FilterValue";
import { SortType } from "../../../models/SortType";
import { WalletTransactionType } from "../../../models/Swagstore/SwagstoreWalletTransactions";
import { SwagstoreWalletStore } from "./SwagstoreWalletStore";
import SwagstoreWalletTransactionsTable from "./SwagstoreWalletTransactionsTable";
import AppButton from "../../components/common/buttons/AppButton";
import { useNotification } from "../../../utils/NotificationsContext";
import {
  getModernPageColors,
  modernDrawerBodySx,
  modernDrawerHeaderSx,
  modernFilterPanelHeaderSx,
  modernFilterPanelSx,
} from "../../../styles/modernListingPageSx";

const SEARCH_DEBOUNCE_MS = 400;
/** Above AppBar (`theme.zIndex.drawer + 1`). */
const DRAWER_Z_OFFSET = 2000;
/** Keep filter poppers/calendars above the drawer modal. */
const getFilterOverlayZIndex = (drawerZIndex: number) => drawerZIndex + 100;

const TRANSACTION_TYPE_OPTIONS: FilterValue[] = [
  { id: WalletTransactionType.CREDIT, value: "Credit" },
  { id: WalletTransactionType.DEBIT, value: "Debit" },
];

interface SwagstoreWalletTransactionsDrawerProps {
  store: SwagstoreWalletStore;
}

const SwagstoreWalletTransactionsDrawer: React.FC<
  SwagstoreWalletTransactionsDrawerProps
> = ({ store }) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const { showError, showSuccess } = useNotification();
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  const filterOverlayZIndex = getFilterOverlayZIndex(
    theme.zIndex.drawer + DRAWER_Z_OFFSET,
  );

  const open = store.transactionsDrawerWalletId !== null;
  const query = store.transactionsQuery;

  useEffect(() => {
    if (!open || !query) return;
    store.fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query]);

  useEffect(() => {
    if (!open) {
      setIsFiltersExpanded(false);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  if (!query) return null;

  const commitSearch = (value: string) => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    const trimmed = value.trim();
    if (trimmed === (query.search || "")) return;
    store.setTransactionsQuery({ search: trimmed || undefined, page_no: 1 });
  };

  const handleSearchInput = (value: string) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(
      () => commitSearch(value),
      SEARCH_DEBOUNCE_MS,
    );
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      commitSearch((e.target as HTMLInputElement).value);
    }
  };

  const selectedType: FilterValue[] = query.type
    ? TRANSACTION_TYPE_OPTIONS.filter((option) => option.id === query.type)
    : [];

  const handleTypeSelect = (items: FilterValue[]) => {
    store.setTransactionsQuery({
      type:
        items.length > 0
          ? (String(items[0].id) as WalletTransactionType)
          : undefined,
      page_no: 1,
    });
  };

  const handleDateRangeSelect = (range: {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  }) => {
    store.setTransactionsQuery({
      start_date: range.startDate
        ? range.startDate.format("YYYY-MM-DD")
        : undefined,
      end_date: range.endDate ? range.endDate.format("YYYY-MM-DD") : undefined,
      page_no: 1,
    });
  };

  const handleSortChange = (sortType: SortType) => {
    store.setTransactionsQuery({ sort_type: sortType });
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => {
    store.setTransactionsQuery({ page_no: page });
  };

  const handleRowsPerPageChange = (limit: number) => {
    store.setTransactionsQuery({ limit, page_no: 1 });
  };

  const clearFilters = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    store.setTransactionsQuery({
      search: undefined,
      type: undefined,
      start_date: undefined,
      end_date: undefined,
      page_no: 1,
    });
  };

  const canDownloadReport = Boolean(query.start_date && query.end_date);
  const downloadDisabledReason =
    "A date range is required to download the report.";

  const handleDownloadReport = () => {
    store
      .downloadTransactionsReport()
      .then((result) => {
        if (result.type === "file") {
          const downloadUrl = window.URL.createObjectURL(result.data);
          const anchor = document.createElement("a");
          anchor.href = downloadUrl;
          anchor.download = result.filename || "wallet-transactions-report.csv";
          document.body.appendChild(anchor);
          anchor.click();
          anchor.remove();
          window.URL.revokeObjectURL(downloadUrl);
          return;
        }

        showSuccess(result.message);
      })
      .catch((error: any) => {
        showError(
          "Download failed",
          error?.response?.data?.message ||
            error?.message ||
            "Something went wrong! Please try again later.",
        );
      });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => store.closeTransactionsDrawer()}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 1200 },
          maxWidth: { xs: "100vw", sm: 1200 },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
      ModalProps={{
        keepMounted: true,
        sx: {
          zIndex: (muiTheme) => muiTheme.zIndex.drawer + DRAWER_Z_OFFSET,
        },
        slotProps: {
          backdrop: {
            sx: {
              zIndex: (muiTheme) =>
                muiTheme.zIndex.drawer + DRAWER_Z_OFFSET - 1,
            },
          },
        },
      }}
    >
      <Box sx={modernDrawerHeaderSx(theme)}>
        <Box display="flex" gap={1.5} alignItems="center" minWidth={0}>
          <AccountBalanceWalletOutlinedIcon
            sx={{ color: theme.palette.customColors.darkBlue, flexShrink: 0 }}
          />
          <Box minWidth={0}>
            <CustomTypography variant="heading5" sx={{ color: colors.title }}>
              Wallet Transactions
            </CustomTypography>
            <CustomTypography
              variant="body2"
              sx={{
                mt: 0.5,
                color: colors.subtitle,
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
              }}
            >
              {store.transactionsDrawerCustomerName || "Transaction history"}
            </CustomTypography>
          </Box>
        </Box>

        <IconButton
          onClick={() => store.closeTransactionsDrawer()}
          size="small"
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ px: { xs: 1.5, sm: 2.5 }, py: 1.5 }}>
        <Paper variant="outlined" sx={modernFilterPanelSx(theme)}>
          <Box
            onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
            sx={modernFilterPanelHeaderSx(theme, isFiltersExpanded)}
          >
            <CustomTypography variant="heading7" sx={{ color: colors.title }}>
              Search & Filters
            </CustomTypography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setIsFiltersExpanded(!isFiltersExpanded);
              }}
            >
              {isFiltersExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={isFiltersExpanded} timeout="auto" unmountOnExit>
            <Grid2 container spacing={2} columns={12} padding={2}>
              <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <CustomTextField
                  width="100%"
                  placeholder="Search by email or reference"
                  value={query.search ?? ""}
                  onSearch={handleSearchInput}
                  onKeyDown={handleSearchKeyDown}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <AppSearchDropdownMulti
                  title="Transaction Type"
                  isMultiSelect={false}
                  value={selectedType}
                  list={TRANSACTION_TYPE_OPTIONS}
                  local={true}
                  minWidth="100%"
                  handleSelect={handleTypeSelect}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <DateRangePickerNew
                  placeholder="Date range"
                  startDate={query.start_date ? dayjs(query.start_date) : null}
                  endDate={query.end_date ? dayjs(query.end_date) : null}
                  onRangeSelect={handleDateRangeSelect}
                  zIndex={filterOverlayZIndex}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}></Grid2>
              <Grid2
                size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                sx={{ display: "flex", gap: 1, width: "100%" }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Tooltip
                    title={downloadDisabledReason}
                    placement="top"
                    arrow
                    disableHoverListener={canDownloadReport}
                    disableFocusListener={canDownloadReport}
                    disableTouchListener={canDownloadReport}
                    slotProps={{
                      popper: {
                        sx: { zIndex: filterOverlayZIndex },
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "block",
                        width: "100%",
                        cursor: canDownloadReport ? undefined : "not-allowed",
                      }}
                    >
                      <AppButton
                        variant="primary"
                        label={
                          store.transactionsDownloadLoading
                            ? "Downloading..."
                            : "Download"
                        }
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadReport}
                        disabled={
                          !canDownloadReport ||
                          store.transactionsDownloadLoading
                        }
                        customStyles={{
                          width: "100%",
                          pointerEvents: canDownloadReport ? "auto" : "none",
                        }}
                      />
                    </Box>
                  </Tooltip>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <AppButton
                    variant="transparentPrimary"
                    label="Clear"
                    onClick={clearFilters}
                    customStyles={{ width: "100%" }}
                  />
                </Box>
              </Grid2>
            </Grid2>
          </Collapse>
        </Paper>
      </Box>

      <Box sx={modernDrawerBodySx()}>
        {store.transactionsError ? (
          <Alert
            severity="error"
            onClose={() => store.clearTransactionsError()}
            sx={{
              mb: 0.5,
              borderRadius: 2,
              border: `1px solid ${getModernPageColors(theme).infoBorder}`,
            }}
          >
            {store.transactionsError}
          </Alert>
        ) : null}
        <SwagstoreWalletTransactionsTable
          rows={store.transactions}
          loading={store.transactionsLoading}
          pageNo={query.page_no}
          limit={query.limit}
          totalCount={store.transactionsTotalCount}
          sortType={query.sort_type}
          walletType={store.transactionsDrawerWalletType}
          onSortChange={handleSortChange}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Box>
    </Drawer>
  );
};

export default observer(SwagstoreWalletTransactionsDrawer);
